import { FC } from "react";
import {
  Box,
  capitalize,
  Fade,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { makeSxStyles } from "ui";
import { paletteColorStringRenderer } from "../../../../utils/string-helpers";
import { AdminGetRoutesLocationCustomerOrderResDto } from "service/api/estabrookSchemas";

interface DropPositionTooltipProps {
  isVisible: boolean;
  position: { x: number; y: number };
  beforeIndex: number;
  afterIndex: number;
  totalItems: number;
  regionName: string;
  regionColor: string;
  serviceTime: string;
  orderDetails?: AdminGetRoutesLocationCustomerOrderResDto;
}

const DropPositionTooltip: FC<DropPositionTooltipProps> = ({
  isVisible,
  position,
  beforeIndex,
  afterIndex,
  totalItems,
  regionName,
  regionColor,
  serviceTime,
  orderDetails,
}) => {
  const styles = useStyles();
  const { palette } = useTheme();

  const paletteColorType = paletteColorStringRenderer(
    palette,
    regionColor.toLowerCase(),
    false,
    "active",
  );

  return (
    <Fade in={isVisible} timeout={150}>
      <Box
        sx={{
          ...styles.tooltipBox,
          position: "fixed",
          left: position.x,
          top: position.y,
          transform: "translate(-50%, -100%)",
          pointerEvents: "none",
        }}
      >
        <Typography sx={styles.tooltipHeading}>
          {orderDetails && <>{capitalize(orderDetails.status)} shift | </>}
          {regionName} region{" "}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          {beforeIndex > 0 && (
            <Box sx={{ ...styles.addressBox, bgcolor: paletteColorType }}>
              <Typography sx={styles.locationSequence}>
                {beforeIndex}
              </Typography>
            </Box>
          )}

          {serviceTime && (
            <Box
              sx={{
                ...styles.addressBox,
                bgcolor: palette.routeStates.ended,
                width: "auto",
                minWidth: "fit-content", // Ensure it doesn't compress
                paddingInline: "6px", // Slightly more padding for service time
              }}
            >
              <Typography sx={styles.locationSequence}>
                {serviceTime}
              </Typography>
            </Box>
          )}
          {afterIndex > 0 && afterIndex !== totalItems + 1 && (
            <Box sx={{ ...styles.addressBox, bgcolor: paletteColorType }}>
              <Typography sx={styles.locationSequence}>
                {!beforeIndex && afterIndex === 2 ? 1 : afterIndex}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Fade>
  );
};

const useStyles = makeSxStyles((theme) => ({
  tooltipBox: {
    backgroundColor: theme.palette.grey[600],
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    padding: "6px 12px",
    borderRadius: "4px",
    zIndex: 1400, // Above most elements
    minWidth: "120px", // Ensure minimum width to prevent content wrapping
    whiteSpace: "nowrap", // Prevent text wrapping in tooltip

    "&::before": {
      content: '""',
      position: "absolute",
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: `6px solid ${theme.palette.grey[600]}`,
      bottom: "-6px",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
  tooltipHeading: {
    marginBottom: "10px",
    color: theme.palette.grey[100],
    whiteSpace: "nowrap", // Prevent heading from wrapping
  },
  addressBox: {
    height: "20px",
    borderRadius: "2px",
    minWidth: "28px", // Ensure minimum width for address boxes
    width: "auto", // Allow auto width for service duration
    padding: "0 4px", // Add horizontal padding for better text spacing
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0, // Prevent boxes from shrinking
  },
  locationSequence: {
    fontSize: "11px",
    color: theme.palette.grey[800],
    fontWeight: 500,
    whiteSpace: "nowrap", // Prevent text wrapping in sequence boxes
  },
}));

export default DropPositionTooltip;
